import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import FileHandler from "@tiptap/extension-file-handler";
import Link from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import { Bold, ExternalLink, Italic, Link2, List, ListOrdered, Paperclip } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { htmlToNoteMarkdown, parseNoteMarkdown, serializeNoteDoc } from "@/lib/noteMarkdown";

/**
 * The Notes dialog's rich editor: bold, italic, links, bullet and numbered
 * lists, saved as Markdown (see lib/noteMarkdown). Keyboard: Ctrl/Cmd+B, I, K;
 * Markdown input rules (`- `, `1. `, `**x**`). Formatting outside that set (underline,
 * headings, colour) has no Markdown the Planner understands, so it is switched
 * off here and dropped from pasted HTML.
 */

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

const IS_MAC = typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || "");
const OPEN_HINT = `${IS_MAC ? "Cmd" : "Ctrl"}+click to open`;

const NoteLink = Link.extend({
  addAttributes() {
    return { ...this.parent?.(), title: { default: OPEN_HINT } };
  },
});

/** Mime types FileHandler passes on (dropped or pasted); the server still validates. */
const ALLOWED_MIME_TYPES = [
  "application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv", "text/plain", "image/png", "image/jpeg", "image/gif", "image/webp",
];


function ToolButton({
  label, title, active, disabled, busy, onClick, children,
}: {
  label: string; title?: string; active?: boolean; disabled?: boolean; busy?: boolean;
  onClick?: () => void; children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={label}
      aria-pressed={active}
      aria-busy={busy || undefined}
      title={title ?? label}
      disabled={disabled}
      // Keep the editor selection: do not take focus from the contenteditable.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn("h-7 w-7 p-0", active && "bg-muted text-foreground")}
    >
      {children}
    </Button>
  );
}

/** Floating toolbar shown while the cursor is inside a link. */
function LinkBubble({ href, onEdit, onRemove }: { href: string; onEdit: () => void; onRemove: () => void }) {
  const safe = SAFE_HREF.test(href);
  return (
    <div
      role="toolbar"
      aria-label={`Link options for ${href}`}
      className="flex items-center gap-1 rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-md"
    >
      {safe ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-7 items-center gap-1 rounded px-2 text-link hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      ) : null}
      <Button type="button" variant="ghost" size="sm" className="h-7 px-2"
        onMouseDown={(e) => e.preventDefault()} onClick={onEdit}>Edit</Button>
      <Button type="button" variant="ghost" size="sm" className="h-7 px-2"
        onMouseDown={(e) => e.preventDefault()} onClick={onRemove}>Remove</Button>
    </div>
  );
}

export function NoteEditor({
  initialMarkdown, onChange, onFiles, attachmentsEnabled = false, uploading = false,
}: {
  /** The note when the dialog opened (the editor is created once with it). */
  initialMarkdown: string;
  /** Called with the current Markdown after every edit. */
  onChange: (markdown: string) => void;
  /** Files chosen with Attach, dropped or pasted: uploaded into the Attachments section, never into the note. */
  onFiles?: (files: File[]) => void;
  /** The backend has a Drive folder configured; otherwise Attach stays disabled. */
  attachmentsEnabled?: boolean;
  /** An upload is running: Attach is busy and drop/paste are ignored. */
  uploading?: boolean;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Read at event time: the editor is created once, the props change.
  const filesRef = useRef<(files: File[]) => void>(() => undefined);
  useEffect(() => {
    filesRef.current = (files) => { if (attachmentsEnabled && !uploading) onFiles?.(files); };
  }, [onFiles, attachmentsEnabled, uploading]);
  const handleFiles = (files: File[]) => filesRef.current(files);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, blockquote: false, code: false, codeBlock: false,
        horizontalRule: false, strike: false, underline: false, trailingNode: false,
        link: false,
      }),
      // The editor's own Link: same options as before, plus a hover hint on every link
      // (the title is a mark attribute default, which a plain HTMLAttributes entry loses to).
      NoteLink.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        isAllowedUri: (url: string) => SAFE_HREF.test(url),
        HTMLAttributes: { class: "text-link cursor-pointer underline underline-offset-2", rel: "noopener noreferrer", target: "_blank" },
      }),
      // Files dropped or pasted into the note upload like the Attach button does. The ref is
      // only read when the event fires, never during render.
      // eslint-disable-next-line react-hooks/refs
      FileHandler.configure({
        allowedMimeTypes: ALLOWED_MIME_TYPES,
        onDrop: (_ed, files) => handleFiles(files),
        onPaste: (_ed, files, html) => { if (!html) handleFiles(files); },
      }),
    ],
    content: parseNoteMarkdown(initialMarkdown),
    editorProps: {
      attributes: {
        class: cn(
          "min-h-[14rem] max-h-[22rem] overflow-y-auto rounded-md border border-input bg-background",
          "px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1",
        ),
        "aria-label": "Notes",
        role: "textbox",
        "aria-multiline": "true",
      },
      handleDOMEvents: {
        // Ctrl/Cmd+click opens a link; a plain click only places the cursor.
        click: (_view, event) => {
          const target = event.target as Node | null;
          const el = target instanceof Element ? target : target?.parentElement;
          const anchor = el?.closest("a");
          if (!anchor) return false;
          event.preventDefault();
          const href = anchor.getAttribute("href") ?? "";
          if ((event.ctrlKey || event.metaKey) && SAFE_HREF.test(href)) {
            window.open(href, "_blank", "noopener,noreferrer");
            return true;
          }
          return false;
        },
      },
      // Pasted HTML becomes the Markdown subset; text outside it is kept as text.
      handlePaste: (_view, event) => {
        const html = event.clipboardData?.getData("text/html");
        if (!html) return false;
        const doc = parseNoteMarkdown(htmlToNoteMarkdown(html));
        event.preventDefault();
        editorRef.current?.commands.insertContent(doc.content);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => onChangeRef.current(serializeNoteDoc(ed.getJSON())),
  });
  // The link under the cursor, kept live for the bubble (the editor does not re-render on selection moves).
  const cursorHref = useEditorState({
    editor,
    selector: ({ editor: ed }) => (ed?.getAttributes("link").href as string | undefined) ?? "",
  });
  const editorRef = useRef(editor);
  useEffect(() => { editorRef.current = editor; }, [editor]);

  if (!editor) return null;

  const openLink = () => {
    setLinkUrl((editor.getAttributes("link").href as string | undefined) ?? "https://");
    setLinkOpen(true);
  };
  const applyLink = () => {
    const href = linkUrl.trim();
    const chain = editor.chain().focus().extendMarkRange("link");
    if (!href) chain.unsetLink().run();
    else if (SAFE_HREF.test(href)) chain.setLink({ href }).run();
    setLinkOpen(false);
  };
  const removeLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setLinkOpen(false);
  };

  // Ctrl/Cmd+K opens the link field (Bold/Italic/lists are TipTap's own keymaps).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      openLink();
    }
  };

  return (
    <div className="space-y-2" onKeyDown={onKeyDown}>
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/30 p-1" role="toolbar" aria-label="Formatting">
        <ToolButton label="Bold" title="Bold (Ctrl+B)" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Italic" title="Italic (Ctrl+I)" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Link" title="Add, edit or remove a link (Ctrl+K)" active={editor.isActive("link")}
          onClick={openLink}>
          <Link2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Bullet list" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Numbered list" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolButton>
        <span className="ml-auto" title={attachmentsEnabled ? undefined : "Attachments are not configured"}>
          <ToolButton
            label="Attach file"
            title={attachmentsEnabled ? "Attach a file (stored in Google Drive)" : "Attachments are not configured"}
            disabled={!attachmentsEnabled || uploading}
            busy={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
          </ToolButton>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (files.length) onFiles?.(files);
            }}
          />
        </span>
      </div>

      {linkOpen ? (
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkOpen(false); }}
            placeholder="https://example.com or mailto:name@example.com"
            aria-label="URL"
            className="h-8 text-sm"
          />
          <Button type="button" size="sm" className="h-8" onClick={applyLink}>Apply</Button>
          {editor.isActive("link") ? (
            <Button type="button" size="sm" variant="outline" className="h-8" onClick={removeLink}>Remove</Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setLinkOpen(false)}>Cancel</Button>
        </div>
      ) : null}

      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: ed }) => ed.isActive("link") && ed.state.selection.empty}
      >
        <LinkBubble
          href={cursorHref ?? ""}
          onEdit={openLink}
          onRemove={removeLink}
        />
      </BubbleMenu>

      <EditorContent editor={editor} />
    </div>
  );
}

export default NoteEditor;
